import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, BookOpen, Users, Plus, Workflow, Package, ShoppingCart, Wallet, LogOut, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/cotizaciones", label: "Cotizaciones", icon: FileText, testid: "nav-cotizaciones" },
  { to: "/crm", label: "CRM", icon: Workflow, testid: "nav-crm" },
  { to: "/catalogo", label: "Catálogo", icon: BookOpen, testid: "nav-catalogo" },
  { to: "/materiales", label: "Materiales", icon: Package, testid: "nav-materiales" },
  { to: "/insumos", label: "Insumos", icon: ShoppingCart, testid: "nav-insumos" },
  { to: "/gastos", label: "Gastos", icon: Wallet, testid: "nav-gastos" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clientes" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const nav = useNavigate();
  const auth = useAuth() || {};
  const { user, logout } = auth;
  const doLogout = () => { logout?.(); nav("/login", { replace: true }); };
  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-[#F4F2EC]/50">
        <div className="px-6 pt-8 pb-6">
          <div className="bg-[#2f3d31] rounded-md p-4 flex items-center justify-center" data-testid="sidebar-logo">
            <img src={LOGO_URL} alt="Madera Blanca Studio" className="w-full max-w-[170px] object-contain" />
          </div>
          <div className="uppercase-label mt-4">Cotizaciones</div>
        </div>
        <nav className="px-3 flex-1 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover-lift ${
                  isActive ? "bg-[#8B5A33] text-white" : "text-foreground/80 hover:bg-white hover:text-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 space-y-2">
          <NavLink to="/cotizaciones/nueva" data-testid="sidebar-new-quote">
            <Button className="w-full bg-[#2C2A28] hover:bg-[#1c1b1a] text-white rounded-md">
              <Plus className="w-4 h-4 mr-2" /> Nueva cotización
            </Button>
          </NavLink>
          <NavLink
            to="/cuenta"
            data-testid="sidebar-cuenta"
            className={({ isActive }) =>
              `flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#8B5A33]/10 text-[#6D4525]" : "text-foreground/80 hover:bg-white"}`
            }
          >
            <UserCog className="w-4 h-4" strokeWidth={1.5} />
            <span className="truncate">{user?.nombre || user?.email || "Mi cuenta"}</span>
          </NavLink>
          <button
            onClick={doLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-[#8a3537] hover:bg-[#BC4749]/10"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Cerrar sesión
          </button>
        </div>
        <div className="px-6 py-4 text-[11px] text-muted-foreground border-t border-border">
          Carpintería fina para interiores
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="bg-[#2f3d31] rounded p-1.5">
            <img src={LOGO_URL} alt="Madera Blanca Studio" className="h-8 object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <NavLink to="/cuenta" title="Mi cuenta" className="p-2 rounded-md border border-border text-[#8B5A33]" data-testid="mobile-cuenta">
              <UserCog className="w-4 h-4" />
            </NavLink>
            <button onClick={doLogout} title="Cerrar sesión" className="p-2 rounded-md border border-border text-[#8a3537]" data-testid="mobile-logout">
              <LogOut className="w-4 h-4" />
            </button>
            <NavLink to="/cotizaciones/nueva">
              <Button size="sm" className="bg-[#2C2A28] hover:bg-[#1c1b1a]" data-testid="mobile-new-quote">
                <Plus className="w-4 h-4 mr-1" /> Nueva
              </Button>
            </NavLink>
          </div>
        </div>
        <div className="flex border-t border-border overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex-1 min-w-fit flex items-center justify-center gap-2 px-4 py-2 text-xs ${
                  isActive ? "text-[#8B5A33] border-b-2 border-[#8B5A33]" : "text-muted-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 lg:pt-0 pt-28" key={location.pathname}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-12">{children}</div>
      </main>
    </div>
  );
}
